/**
 * Utility functions for resolution calculations and status management
 */

const ADD_DELETE_MONTH_START = 10; // October

/**
 * Calculate progress percentage for a resolution
 * @param {Object} resolution - The resolution object
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgress = (resolution) => {
	if (!resolution) return 0;
	if (resolution.type === 'binary') {
		return resolution.completed ? 100 : 0;
	}

	if (resolution.type === 'metric') {
		// Determine current value (latest measurement or startValue)
		const start = typeof resolution.startValue === 'number' ? resolution.startValue : null;
		const target = typeof resolution.targetValue === 'number' ? resolution.targetValue : null;
		if (start === null || target === null) return 0;
		const current = resolution.currentValue;

		if (target === start) return 0;
		// progress calculation depends on direction
		if (target > start) {
			const pct = ((current - start) / (target - start)) * 100;
			return Math.round(Math.max(0, Math.min(100, pct)));
		} else {
			const pct = ((start - current) / (start - target)) * 100;
			return Math.round(Math.max(0, Math.min(100, pct)));
		}

		// Streaks are not part of overall progress calculation
		if (resolution.type === 'streak') {
			return 0;
		}
	}

	if (!resolution.count || resolution.count === 0) return 0;
	return Math.round((resolution.completedCount / resolution.count) * 100);
};

/**
 * Calculate status for all resolutions based on progress and timeline
 * @param {Array} resolutions - Array of resolution objects
 * @returns {Array} Updated resolutions with status information
 */
export const calculateStatus = (resolutions, t) => {
	resolutions.forEach((resolution) => {
		// Calculate progress percentage
		resolution.progress = calculateProgress(resolution);

		const currentDate = new Date();
		const currentDay = Math.floor(
			(currentDate - new Date(currentDate.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24),
		);
		const currentYear = currentDate.getFullYear();

		// Determine status differently for binary, metric vs count-based resolutions
		if (resolution.type === 'binary') {
			if (resolution.completedCount && resolution.completedCount > 0) {
				resolution.statusCode = 'completed';
				resolution.status = t('completed');
			} else if (resolution.year > currentYear) {
				resolution.statusCode = 'future';
				resolution.status = t('future');
			} else {
				resolution.statusCode = 'on-track';
				resolution.status = t('on-track');
			}
		} else if (resolution.type === 'metric') {
			// For metric types, compare progress vs expected by day of year
			const progress = resolution.progress || 0;
			const expectedPct = Math.round((currentDay / 365) * 100);

			if (progress >= 100) {
				resolution.statusCode = 'completed';
				resolution.status = t('completed');
			} else if (resolution.year > currentYear) {
				resolution.statusCode = 'future';
				resolution.status = t('future');
			} else if (progress > expectedPct + 10) {
				resolution.statusCode = 'ahead';
				resolution.status = t('ahead');
			} else if (progress < expectedPct - 10) {
				resolution.statusCode = 'behind';
				resolution.status = t('behind');
			} else {
				resolution.statusCode = 'on-track';
				resolution.status = t('on-track');
			}
		} else {
			const maxDaysEstimatedToCompleteOneItem =
				resolution.count && resolution.count > 0 ? Math.round(365 / resolution.count) : 365;
			const currentCompletionCount = Math.floor(currentDay / maxDaysEstimatedToCompleteOneItem) || 0;

			if (resolution.count && resolution.completedCount >= resolution.count) {
				resolution.statusCode = 'completed';
				resolution.status = t('completed');
			} else if (resolution.completedCount > currentCompletionCount) {
				resolution.statusCode = 'ahead';
				resolution.status = t('ahead');
			} else if (resolution.completedCount < currentCompletionCount) {
				resolution.statusCode = 'behind';
				resolution.status = t('behind');
			} else if (resolution.year > currentYear) {
				resolution.statusCode = 'future';
				resolution.status = t('future');
			} else {
				resolution.statusCode = 'on-track';
				resolution.status = t('on-track');
			}
		}
	});
	return resolutions;
};

/**
 * Get smart default resolution year based on current date
 * @returns {number} Recommended resolution year
 */
export const getSmartResolutionYear = () => {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentMonth = now.getMonth() + 1;

	// If we're in the final 3 months (Oct, Nov, Dec), default to next year
	// If we're in January, default to current year
	let defaultYear;
	if (currentMonth >= ADD_DELETE_MONTH_START) {
		// October, November, December - set to next year
		defaultYear = currentYear + 1;
	} else if (currentMonth === 1) {
		// January - set to current year
		defaultYear = currentYear;
	} else {
		// Other months - default to current year
		defaultYear = currentYear;
	}

	return defaultYear;
};

/**
 * Check if updates are allowed for a resolution in a specific year
 * @param {number} resolutionYear - The year of the resolution
 * @param {Object} currentUser - Current authenticated user
 * @param {Object} person - Person object containing user data
 * @param {boolean} isAdmin - Whether current user is admin
 * @returns {boolean} Whether updates are allowed
 */
export const isUpdateAllowed = (resolutionYear, currentUser, person, isAdmin) => {
	return true;
	/*
	// First check if user has permission
	if (!currentUser || !person) return false;
	if (!(currentUser.uid === person.userId || currentUser.email === person.email || isAdmin)) {
		return false;
	}

	// Then check if timing allows updates for this year
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11, we want 1-12

	// Allow updates if we're in the resolution's year
	if (currentYear === resolutionYear) {
		return true;
	}

	// Allow updates if we're in the first month (January) of the following year
	if (currentYear === resolutionYear + 1 && currentMonth === 1) {
		return true;
	}

	// Otherwise, updates are not allowed
	return false;
	*/
};

/**
 * Check if adding/deleting resolutions is allowed
 * @param {Object} currentUser - Current authenticated user
 * @param {Object} person - Person object containing user data
 * @returns {boolean} Whether add/delete operations are allowed
 */
export const isAddAllowed = (currentUser, person) => {
	return true;
	/*
	// First check if user has permission
	if (!currentUser || !person) return false;
	if (!(currentUser.uid === person.userId || currentUser.email === person.email)) {
		return false;
	}

	// Then check timing based on original logic
	const now = new Date();
	const currentMonth = now.getMonth() + 1;
	const currentYear = now.getFullYear();

	// Determine the target year for the person's resolutions
	// Prefer person.targetYear, fallback to getSmartResolutionYear if not present
	const targetYear = person.targetYear || getSmartResolutionYear();

	// Allow add/delete if we're in the final 3 months (Oct, Nov, Dec) of the year BEFORE the target year
	if (currentYear === targetYear - 1 && currentMonth >= ADD_DELETE_MONTH_START) {
		return true;
	}

	// Allow add/delete if we're in the first month (January) of the target year
	if (currentYear === targetYear && currentMonth === 1) {
		return true;
	}

	// Otherwise, not allowed
	return false;
	*/
};

export const isDeleteAllowed = (currentUser, person, resolutionYear) => {
	return true;
	/*
	// First check if user has permission
	if (!currentUser || !person) return false;
	if (!(currentUser.uid === person.userId || currentUser.email === person.email)) {
		return false;
	}

	// Then check timing based on original logic
	const now = new Date();
	const currentMonth = now.getMonth() + 1;
	const currentYear = now.getFullYear();

	// Determine the target year for the person's resolutions
	// Prefer person.targetYear, fallback to getSmartResolutionYear if not present
	const targetYear = resolutionYear;

	// Allow add/delete if we're in the final 3 months (Oct, Nov, Dec) of the year BEFORE the target year
	if (currentYear === targetYear - 1 && currentMonth >= ADD_DELETE_MONTH_START) {
		return true;
	}

	// Allow add/delete if we're in the first month (January) of the target year
	if (currentYear === targetYear && currentMonth === 1) {
		return true;
	}

	// Otherwise, not allowed
	return false;
	*/
};

/**
 * Get completed count for a resolution with bounds checking
 * @param {Object} resolution - The resolution object
 * @returns {number} Completed count within valid bounds
 */
export const getCompletedCountForResolution = (resolution) => {
	if (!resolution) return 0;
	if (resolution.type === 'binary') {
		return resolution.completed ? 1 : 0;
	}
	return resolution.completedCount > (resolution.count || 1) ? resolution.count || 1 : resolution.completedCount || 0;
};

/**
 * Get background CSS class based on status code
 * @param {string} statusCode - Status code (completed, ahead, behind, on-track)
 * @returns {string} CSS class name
 */
export const getBgClassByStatusCode = (statusCode) => {
	if (statusCode === 'completed') return 'bg-green-400';
	if (statusCode === 'ahead') return 'bg-blue-400';
	if (statusCode === 'behind') return 'bg-red-400';
	if (statusCode === 'on-track') return 'bg-yellow-400';
	return 'bg-gray-400'; // default
};

/**
 * Calculate time left until the start of the target year (New Year's Day)
 * @param {number} targetYear - The target year
 * @returns {string} Formatted time left string
 */
export const calculateTimeLeft = (targetYear) => {
	const now = new Date();
	const startOfTargetYear = new Date(targetYear, 0, 1, 0, 0, 0); // January 1st, 00:00:00
	const timeDiff = startOfTargetYear - now;

	if (timeDiff <= 0) {
		return '0 months, 0 days, 0 hours, 0 minutes, 0 seconds';
	}

	let months =
		startOfTargetYear.getMonth() - now.getMonth() + 12 * (startOfTargetYear.getFullYear() - now.getFullYear());
	let days = startOfTargetYear.getDate() - now.getDate();
	let hours = startOfTargetYear.getHours() - now.getHours();
	let minutes = startOfTargetYear.getMinutes() - now.getMinutes();
	let seconds = startOfTargetYear.getSeconds() - now.getSeconds();

	if (seconds < 0) {
		seconds += 60;
		minutes--;
	}
	if (minutes < 0) {
		minutes += 60;
		hours--;
	}
	if (hours < 0) {
		hours += 24;
		days--;
	}
	if (days < 0) {
		months--;
		const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
		days += previousMonth.getDate();
	}

	return `${months} months, ${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
};
